import React, { useEffect } from 'react'
import { connect } from 'react-redux'
import { Route, Switch, withRouter } from 'react-router-dom'
import { bindActionCreators } from 'redux'
import { loginUser, logoutUser, receiveLogin } from './actions'
import { fetchAppContext } from './actions/app_context'
import { fetchFeatureFlag } from './actions/feature_flag'
import { disableTransactionSort } from './actions/tx_grid_sort'
import { updateBusinessSettings } from './actions/update_business_settings'
import { updateProfile } from './actions/update_profile'
import { getUser } from './actions/users'
import PageHeading from './components/common/page_heading'
import ErrorBoundary from './components/error/error_boundary'
import LoginPage from './components/landing_page/login_page'
import TxVisualization from './components/metrics/tx_visualization'
import Navbar from './components/navbar'
import SideBar from './components/sidebar'
import {
  __isEnvDev,
  APP_LAST_VISITED_USER_TX_URL,
  APP_USER,
  navigationItems,
  SESSION_APP_CONTEXT,
  STORAGE_TYPE_LOCAL,
  USER_TX_DETAILS_PARENT_URI,
} from './config'
import AccessCodes from './containers/access-codes'
import AccreditationStatuses from './containers/accreditation_statuses'
import AddUser from './containers/add_user'
import Business from './containers/business'
import BusinessSettings from './containers/business_settings'
import Businesses from './containers/businesses'
import Documentation from './containers/documentation/documentation'
import { ESign } from './containers/e_sign'
import EditUser from './containers/edit_user'
import Insights from './containers/insights/insights'
import { ManualAml } from './containers/manual-aml'
import NotFound from './containers/not_found'
import Notification from './containers/notification'
import Notifications from './containers/notifications/notifications'
import Profile from './containers/profile'
import ResetPassword from './containers/reset_password'
import SelfieCheck from './containers/selfieCheck'
import SupportedDocuments from './containers/supported_documents'
import TxDetails from './containers/txDetails/tx_details'
import UserTransactions from './containers/user_transactions'
import Users from './containers/users'
import WizardForm from './containers/wizard_form'
import StorageProviderFactory from './managers/storage_provider_factory'
import { APP_FF } from './middleware/api'
import { appContextFromResponse, currentAppContext } from './utils/app_context'
import { isClientAdmin, isManualPageAvailable, isStaff } from './utils/helpers'
import MenuTreeParser from './utils/menu_tree_parser'
import ProtectedRoute from './utils/protected_route'
import { isResetPasswordPath, RESET_PASSWORD_PATH } from './utils/routes'

const errorFallback = <ErrorBoundary />

export const App = (props) => {
  const {
    isAuthenticated,
    errorMessage,
    isLogoutSuccess,
    location,
    navigate,
    appContextResults,
    ephemeralToken,
    fetchAppContext,
    disableTransactionSort,
    logoutUser,
  } = props
  const currentYear = new Date().getFullYear()

  useEffect(() => {
    if (isAuthenticated) {
      fetchAppContext()
    }
    // fetchAppContext is rebound on every route change (mapDispatchToProps uses ownProps); listing it would refetch on each navigation
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated])

  // setup things on login
  const handleLogin = (credentilas) => {
    const storageProvider = StorageProviderFactory.create(STORAGE_TYPE_LOCAL)
    const { loginUser, receiveLogin, fetchAppContext, fetchFeatureFlag, navigate, history } = props

    let redirectURL
    if (history && history.location.state && history.location.state.redirect_url) {
      redirectURL = history.location.state.redirect_url
    }

    Promise.resolve(loginUser(credentilas))
      .then(async (loggedIn) => {
        if (!loggedIn) {
          return
        }
        // fetch App Context
        Promise.resolve(fetchAppContext())
          .then((data) => {
            storageProvider.setItem(SESSION_APP_CONTEXT, JSON.stringify(appContextFromResponse(data.response)))
          })
          .catch((reason) => {
            if (__isEnvDev()) {
              console.log('Error encountered while trying to fetch app context: ', reason)
            }
          })
        // store ff
        Promise.resolve(fetchFeatureFlag())
          .then((data) => {
            if (!data.response || !Object.keys(data.response).length) {
              return
            }
            storageProvider.setItem(APP_FF, JSON.stringify(data.response))
          })
          .catch((reason) => {
            if (__isEnvDev()) {
              console.log('Error encountered while trying to fetch feature flag: ', reason)
            }
          })

        // store user for roles etc
        try {
          const data = await props.getUser()
          const user = data.response
          if (user) {
            storageProvider.setItem(APP_USER, JSON.stringify(user))
            if (redirectURL) {
              props.history.push(redirectURL)
            }
          }
        } catch (reason) {
          if (__isEnvDev()) {
            console.log('Error encountered while trying to fetch user: ', reason)
          }
        }

        if (!window.location.pathname.startsWith(USER_TX_DETAILS_PARENT_URI)) {
          const lastVisitedURL = storageProvider.getItem(APP_LAST_VISITED_USER_TX_URL)
          if (lastVisitedURL) {
            navigate(lastVisitedURL)
          }
        }

        receiveLogin()
      })
      .catch((reason) => {
        if (__isEnvDev()) {
          console.log('Error encountered while trying to login %s', reason)
        }
      })
  }

  const getPageTitle = () => {
    let currentBasePath = '/'
    const arr = location.pathname.split('/').filter((item) => item !== '')
    if (arr.length) {
      currentBasePath = '/' + arr[0]
    }

    const pageTitle = (MenuTreeParser.parse(navigationItems || [], currentBasePath, []) || [])
      .filter((item) => currentBasePath.startsWith(item.path))
      .map((item) => ({ title: item.title }))

    if (Array.isArray(pageTitle) && pageTitle.length) {
      return pageTitle[0].title
    }
    return ''
  }

  const isClerk = (user) => !!(user && user.groups && user.groups.includes('clerks'))

  const logout = () => {
    disableTransactionSort()
    logoutUser()
  }

  const pageTitle = getPageTitle()
  const isResetPasswordRoute = isResetPasswordPath(location.pathname)
  const appContext = currentAppContext(appContextResults)
  const company = appContext ? appContext.getName() : null
  const logo = appContext ? appContext.getLogo() : null

  const user = JSON.parse(StorageProviderFactory.create(STORAGE_TYPE_LOCAL).getItem(APP_USER))
  return (
    <div id='wrapper'>
      {(!isAuthenticated || isResetPasswordRoute) && (
        <ErrorBoundary fallback={errorFallback} showDialog={true}>
          <main>
            {!isAuthenticated && (
              <Route
                onEnter
                exact
                path='/'
                component={() => (
                  <LoginPage
                    isLogoutSuccess={isLogoutSuccess}
                    errorMessage={errorMessage}
                    ephemeralToken={ephemeralToken}
                    onLoginClick={handleLogin}
                  />
                )}
              />
            )}
            <Route exact path={RESET_PASSWORD_PATH} component={ResetPassword} />
          </main>
        </ErrorBoundary>
      )}
      {isAuthenticated && !isResetPasswordRoute && (
        <SideBar user={user} logo={logo} company={company} onLogoutClick={logout} />
      )}
      {isAuthenticated && !isResetPasswordRoute && (
        <div id='page-wrapper' className='gray-bg'>
          <div className='row border-bottom white-bg'>
            <Navbar
              company={company}
              isAuthenticated={isAuthenticated}
              onLogoutClick={logout}
              onNavigateHomeClick={navigate}
            />
          </div>
          {isAuthenticated && <PageHeading pageTitle={pageTitle} navigate={navigate} location={location} />}
          <ErrorBoundary fallback={errorFallback} showDialog={true}>
            {isAuthenticated && (
              <main className='wrapper wrapper-content'>
                <Switch>
                  <Route exact path='/' component={UserTransactions} />
                  <Route exact path='/user-transactions' component={UserTransactions} />
                  <Route exact path='/tx-details/:id' render={(props) => <TxDetails {...props} user={user} />} />
                  <Route exact path='/businesses' component={Businesses} />
                  <Route exact path='/business/:id' component={Business} />
                  <Route exact path='/accreditation-statuses' component={AccreditationStatuses} />
                  <Route exact path='/e-sign' component={ESign} />
                  <Route exact path='/profile' component={Profile} />
                  <Route exact path='/notifications' component={Notifications} />
                  <Route exact path='/notifications/:id' component={Notification} />
                  <ProtectedRoute
                    exact
                    path='/business-settings'
                    component={BusinessSettings}
                    isAccessible={isClientAdmin(user) || isStaff(user)}
                    redirectToPath={'/'}
                  />
                  <Route exact path='/metrics2' component={TxVisualization} />
                  <Route exact path='/documentation' component={Documentation} />
                  <Route exact path='/supported-documents' component={SupportedDocuments} />
                  <Route exact path='/face-confirmation' component={SelfieCheck} />
                  <ProtectedRoute
                    exact
                    path='/wizard/:id'
                    component={WizardForm}
                    isAccessible={isClientAdmin(user) || !isClerk(user)}
                    redirectToPath={'/'}
                  />
                  <ProtectedRoute
                    exact
                    path='/users'
                    component={Users}
                    isAccessible={isClientAdmin(user)}
                    redirectToPath={'/'}
                  />
                  <ProtectedRoute
                    exact
                    path='/users/add'
                    component={AddUser}
                    isAccessible={isClientAdmin(user)}
                    redirectToPath={'/'}
                  />
                  <ProtectedRoute
                    exact
                    path='/users/:id'
                    component={EditUser}
                    isAccessible={isClientAdmin(user)}
                    redirectToPath={'/'}
                  />

                  {isStaff(user) && <Route path='/insights' component={Insights} />}
                  <ProtectedRoute
                    exact
                    path='/manual-aml'
                    component={ManualAml}
                    isAccessible={isManualPageAvailable(user)}
                    redirectToPath={'/'}
                  />
                  {isStaff(user) && <Route exact path='/access-codes' component={AccessCodes} />}
                  <Route component={NotFound} />
                </Switch>
              </main>
            )}
          </ErrorBoundary>
          <div className='footer'>
            <div className='pull-right'>{process.env.REACT_APP_VERSION ? `v${process.env.REACT_APP_VERSION}` : ''}</div>
            <div>
              <strong>Copyright</strong> Netki &copy; 2017 - {currentYear}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// These props come from the application's
// state when it is started
const mapStateToProps = (state) => {
  const { tx, auth, appContext, featureFlag } = state
  const { userTxs } = tx
  const { isAuthenticated, errorMessage, isTokenExpired, isLogoutSuccess, ephemeralToken } = auth
  const { results } = appContext
  const { data } = featureFlag
  return {
    userTxs,
    isAuthenticated,
    errorMessage,
    isTokenExpired,
    isLogoutSuccess,
    ephemeralToken,
    appContextResults: results,
    featureFlagResults: data,
  }
}

const mapDispatchToProps = (dispatch, ownProps) =>
  bindActionCreators(
    {
      disableTransactionSort,
      logoutUser,
      loginUser,
      receiveLogin,
      fetchAppContext,
      fetchFeatureFlag,
      getUser,
      updateProfile,
      updateBusinessSettings,
      navigate: (path) => ownProps.history.push(path),
    },
    dispatch
  )

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(App))
